import React, { useRef, useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import AddressField from './formfields/AddressField';
import { usePricingApi } from '../hooks/usePricingApi';
import { ThemeProvider } from '../context/ThemeContext';
import TripTypeIcon from './TripTypeIcon';
import SequenceTooltip from './SequenceTooltip';
import { useFormStore } from '../stores/formData';

const sequenceSteps = [
  {
    id: 'pickup-address',
    content: 'Enter your origin city to begin your pricing estimate.',
    position: 'top'
  },
  {
    id: 'destination-address', 
    content: 'Enter your destination city. We\'ll deliver your bags directly to your location.',
    position: 'top'
  },
  {
    id: 'book-now',
    content: 'Click book now to get started with your booking!',
    position: 'top'
  },
];

const PricingSpinner = ({ size = 16 }) => (
  <div 
    className="inline-block animate-spin rounded-full border-2 border-gray-300 border-t-blue-600"
    style={{ width: size, height: size }}
  />
);
PricingSpinner.propTypes = {
  size: PropTypes.number
};

const BookingWidget = ({ partnerConfig, onBook, className, startingPrice: defaultPrice, currency }) => {
  const [startingPrice, setStartingPrice] = useState(null);
  const [canBookNow, setCanBookNow] = useState(false);  
  const [pricingPayload, setPricingPayload] = useState(null);
  const [showSequence, setShowSequence] = useState(false);
  const pickupRef = useRef(null);
  const destinationRef = useRef(null);
  const bookNowRef = useRef(null);
  const actions = useFormStore((state) => state.actions);

  useEffect(() => {
    actions.form.reset();
    actions.form.setCurrentPage(null, 'widget');
  }, []);

  const stepRefs = {
    'pickup-address': pickupRef,
    'destination-address': destinationRef,
    'book-now': bookNowRef
  };

  const [fromLocation, setFromLocation] = useState({
    city: 'Miami',
    state: 'FL',
    country: 'USA',
    formatted_address: 'Miami, FL, USA'
  });
  const [toLocation, setToLocation] = useState({
    city: 'Amsterdam',
    state: '',
    country: 'NL',
    formatted_address: 'Amsterdam, NL'
  });
  
  const { getDefaultPricing, loading: pricingLoading } = usePricingApi();
  
  const handleBookNow = useCallback(() => {
    if (onBook) {
      onBook({
        from: fromLocation,
        to: toLocation,
        pricing: pricingPayload
      });
    }
  }, [onBook, fromLocation, toLocation, startingPrice]);

  const handleFromLocationChange = useCallback((address) => {
    setFromLocation(address);
  }, []);

  const handleToLocationChange = useCallback((address) => {
    setToLocation(address);
  }, []);

  /**
   * Extracts the lowest price across all products from the pricing payload
   * @param {Object} pricingData - The pricing data payload
   * @returns {Object} - Object containing lowest price info
   */
  const getLowestPrice = (pricingData) => {
    if (!pricingData?.rates || !Array.isArray(pricingData.rates)) {
      return { price: null, product: null, error: 'No rates data found' };
    }

    setPricingPayload(pricingData);

    let lowestPrice = Infinity;
    let lowestPriceProduct = null;

    // Iterate through all product lines (rates)
    pricingData.rates.forEach(rate => {
      if (rate.products && Array.isArray(rate.products)) {
        // Check each product in the product line
        rate.products.forEach(product => {
          if (product.price_cents && product.price_cents < lowestPrice) {
            lowestPrice = product.price_cents;
            lowestPriceProduct = product;
          }
        });
      }
    });

    if (lowestPrice === Infinity) {
      return { price: null, product: null, error: 'No products with pricing found' };
    }

    return {
      price: lowestPrice, // Price in cents
      priceFormatted: `$${(lowestPrice / 100).toFixed(2)}`, // Formatted price
      product: lowestPriceProduct
    };
  };

  useEffect(() => {
    const fetchPricing = async () => {
      // load default pricing
      const fromPlaceId = fromLocation?.google_place_id || fromLocation?.place_id || fromLocation?.place?.id;
      const toPlaceId = toLocation?.google_place_id || toLocation?.place_id || toLocation?.place?.id;
      if (fromPlaceId && toPlaceId) {
        // Fetch pricing based on locations
        const pricingParams = {
          origin: {
            id: fromPlaceId,
            type: 'GooglePlace'
          },
          destination: {
            id: toPlaceId,
            type: 'GooglePlace'
          },
          brand_id: window.gon?.current_brand || 'shipplay',
          partner_id: window.gon?.partner_id,
          direction: { id: 'direction', type: 'direction' }
        }
        try {
          const pricingData = await getDefaultPricing(pricingParams);
          if (pricingData && pricingData?.data?.rates) {
            const lowestPrice = getLowestPrice(pricingData.data);
            setStartingPrice(Math.floor(lowestPrice.price / 100));
            setCanBookNow(true)
          }
        } catch (error) {
          console.error('Error fetching pricing data:', error);
        }
      }
    }
    fetchPricing();
  }, [fromLocation, toLocation]);

  const handleSequenceComplete = () => {
  };

  const handleSequenceSkip = () => {
  };

  const handleSequenceReset = () => {
    sessionStorage.removeItem('sequence-widget-tutorial-completed');
  };

  return (
    <ThemeProvider defaultTheme="light">
      <div className={clsx(
        'w-[90%] m-2 mb-8 md:w-[752px] bg-white p-6 text-card-foreground shadow-sm',
        'flex-col md:flex-row justify-center mx-auto rounded-lg border border-gray-200',
        className
      )}>
        {/* Location Fields Section */}
        <div className="w-full flex flex-col md:flex-row justify-center items-center mb-6">
          {/* Origin */}
          <div className="w-full md:flex-1 mb-2 md:mb-0">
            <a ref={pickupRef} name="pickupRef"></a>
            <label className="text-left block text-sm font-medium text-gray-700 mb-2">
              Origin
            </label>
            <AddressField
              id={'widget-from-address'}
              placeholder="Miami, FL"
              onAddressChange={handleFromLocationChange}
            />
          </div>

          {/* Swap Icon */}
          <div className="md:w-[50px] flex items-center justify-center">
             <div className="md:hidden m-2 w-8 h-8 flex items-center justify-center">
              <TripTypeIcon color="gray" orientation="vertical" />
            </div>
            <div className="hidden md:block pl-1 pt-5 w-8 h-8 flex items-center justify-center">
              <TripTypeIcon color="gray" orientation="horizontal" />
            </div>
          </div>

          {/* Destination */}
          <div className="w-full md:flex-1">
            <label className="text-left block text-sm font-medium text-gray-700 mb-2">
              Destination
            </label>
            <a ref={destinationRef} name="destinationRef"></a>
            <AddressField
              id={'widget-to-address'}
              placeholder="Amsterdam, NL"
              onAddressChange={handleToLocationChange}
            />
          </div>
        </div>

        {/* Pricing Section */}
        {(pricingLoading || startingPrice)&& 
          <div className="pt-4 mb-6">
            <div className="flex items-center justify-center">
              <div className="mr-2 flex-1 h-px bg-gray-300"></div>
              <span className="text-sm text-gray-600 mr-2">From</span>
              <div className="text-2xl font-bold text-green-600">
                {pricingLoading && <PricingSpinner size={20} />}
                {!pricingLoading && startingPrice && (
                  <>
                    {currency}{startingPrice}
                  </>
                )}
                {!pricingLoading && !startingPrice && (
                  <>
                    {currency}{defaultPrice}
                  </>
                )}
              </div>
              <span className="text-sm text-gray-600 ml-2">each way</span>
              <div className="ml-2 flex-1 h-px bg-gray-300"></div>
            </div>
          </div>
        }

        {/* Book Now Button */}
        <a ref={bookNowRef} name="bookNowRef"></a>
        <button
          onClick={handleBookNow}
          disabled={!canBookNow}
          className={clsx(
            'w-full bg-slate-800 hover:bg-slate-700 text-white font-medium',
            'py-4 px-4 rounded-md transition-colors duration-200',
            {
              'opacity-50 cursor-not-allowed hover:bg-slate-800': !canBookNow
            }
          )}
        >
          Book now
        </button>

        <div className="mt-4 text-sm text-gray-900 text-center">
          {`Or call our cruise concierge team to book at ${partnerConfig.supportPhoneNumber || '(855) 566-2085'}.`}
        </div>

        {showSequence && 
          <SequenceTooltip
            id="widget-tutorial"
            steps={sequenceSteps}
            refs={stepRefs}
            startDelay={1000}
            stepDelay={500}
            enabled={false}
            onComplete={handleSequenceComplete}
            onSkip={handleSequenceSkip}
            onReset={handleSequenceReset}
          />
  }
      </div>
    </ThemeProvider>  
  );
};

// PropTypes validation
BookingWidget.propTypes = {
  partnerConfig: PropTypes.object.isRequired,
  onBook: PropTypes.func,
  className: PropTypes.string,
  startingPrice: PropTypes.number,
  currency: PropTypes.string,
};

// Default props
BookingWidget.defaultProps = {
  onBook: null,
  className: '',
  startingPrice: 45,
  currency: '$',
};

export default BookingWidget;